// Feltkalkulatorer: ett vindu som samler de rene beregnings-motorene fra
// @skade/contracts og gjør dem brukbare i COP-en — ballistikk/DOPE, luftdropp
// (CARP), HLZ-egnethet og GARS-rutenett. Ren DOM (felt-verdier via textContent);
// all matematikk ligger i kontraktene (testet), panelet er tynn inn/ut-visning.
import {
  dopeCard, bcImperialToMetric,
  computeCarp, CANOPIES,
  assessHlz, HELO_CLASS_LABEL, HLZ_SUITABILITY_LABEL,
  toGars,
  type HeloClass, type HlzSurface,
} from '@skade/contracts';

type Tool = 'dope' | 'carp' | 'hlz' | 'gars';
const TOOLS: { id: Tool; label: string }[] = [
  { id: 'dope', label: 'Ballistikk (DOPE)' },
  { id: 'carp', label: 'Luftdropp (CARP)' },
  { id: 'hlz', label: 'Helikopter-LZ' },
  { id: 'gars', label: 'GARS-rutenett' },
];

function div(cls: string): HTMLDivElement { const d = document.createElement('div'); d.className = cls; return d; }
function span(cls: string, text: string): HTMLSpanElement { const s = document.createElement('span'); s.className = cls; s.textContent = text; return s; }
function btn(label: string, on: () => void): HTMLButtonElement { const b = document.createElement('button'); b.textContent = label; b.onclick = on; return b; }

/** Merket tallfelt. Returnerer input-elementet (les .value → Number). */
function numField(parent: HTMLElement, label: string, value: number, step = 'any'): HTMLInputElement {
  const wrap = div('fc-field');
  wrap.append(span('fc-label', label));
  const inp = document.createElement('input');
  inp.type = 'number'; inp.step = step; inp.value = String(value);
  wrap.append(inp); parent.append(wrap);
  return inp;
}
function selField<T extends string>(parent: HTMLElement, label: string, opts: { v: T; t: string }[], sel: T): HTMLSelectElement {
  const wrap = div('fc-field');
  wrap.append(span('fc-label', label));
  const s = document.createElement('select');
  for (const o of opts) { const op = document.createElement('option'); op.value = o.v; op.textContent = o.t; if (o.v === sel) op.selected = true; s.append(op); }
  wrap.append(s); parent.append(wrap);
  return s;
}

export class FieldCalcPanel {
  readonly el: HTMLElement;
  private open = false;
  private tool: Tool = 'dope';
  private formEl!: HTMLElement;
  private outEl!: HTMLElement;

  constructor() {
    this.el = this.build();
    this.renderForm();
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); }

  private build(): HTMLElement {
    const p = div('report-composer fieldcalc-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Feltkalkulatorer'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    const tabs = div('fc-tabs');
    for (const t of TOOLS) {
      const b = btn(t.label, () => { this.tool = t.id; this.syncTabs(tabs); this.renderForm(); });
      b.dataset.tool = t.id;
      tabs.append(b);
    }
    p.append(tabs);
    this.formEl = div('fc-form'); p.append(this.formEl);
    this.outEl = div('fc-out'); p.append(this.outEl);
    this.syncTabs(tabs);
    return p;
  }

  private syncTabs(tabs: HTMLElement) {
    tabs.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.classList.toggle('active', b.dataset.tool === this.tool));
  }

  private renderForm() {
    this.formEl.textContent = '';
    this.outEl.textContent = '';
    if (this.tool === 'dope') this.formDope();
    else if (this.tool === 'carp') this.formCarp();
    else if (this.tool === 'hlz') this.formHlz();
    else this.formGars();
  }

  // ── Ballistikk / DOPE ──────────────────────────────────────────────────
  private formDope() {
    const f = this.formEl;
    const mv = numField(f, 'Munningsfart (m/s)', 790);
    const bc = numField(f, 'BC G1 (lb/in²)', 0.505);
    const zero = numField(f, 'Nullavstand (m)', 100);
    const max = numField(f, 'Maks hold (m)', 800);
    const wind = numField(f, 'Vind (m/s)', 0);
    const windDir = numField(f, 'Vindretning fra (°)', 90);
    f.append(btn('Beregn DOPE', () => {
      const rows = dopeCard(
        { muzzleVelocityMps: +mv.value, bcMetric: bcImperialToMetric(+bc.value), massKg: undefined },
        { zeroRangeM: +zero.value, maxRangeM: +max.value, stepM: 100, wind: { speedMps: +wind.value, fromDeg: +windDir.value } },
      );
      const head = ['Hold', 'Fall', 'Elev', 'Vind', 'Fart', 'Mach', 'ToF'];
      const body = rows.map((r) => [
        `${r.rangeM} m`, `${r.dropCm.toFixed(0)} cm`, `${r.elevationMil.toFixed(1)} mil`,
        `${r.windageMil.toFixed(1)} mil`, `${r.velocityMps.toFixed(0)} m/s`, `M${r.machNumber.toFixed(2)}`, `${r.timeOfFlightS.toFixed(2)} s`,
      ]);
      this.table(head, body);
    }));
  }

  // ── Luftdropp / CARP ───────────────────────────────────────────────────
  private formCarp() {
    const f = this.formEl;
    const lat = numField(f, 'DZ bredde (°)', 59.4);
    const lon = numField(f, 'DZ lengde (°)', 10.6);
    const alt = numField(f, 'Slipphøyde AGL (m)', 300);
    const hdg = numField(f, 'Flyets kurs (°)', 90);
    const spd = numField(f, 'Flyets fart (kt)', 130);
    const wind = numField(f, 'Vind (kt)', 15);
    const windDir = numField(f, 'Vind fra (°)', 270);
    const canopy = selField(f, 'Last/kalesje', CANOPIES.map((c) => ({ v: c.id, t: c.name })), CANOPIES[0].id);
    f.append(btn('Beregn slippunkt', () => {
      const r = computeCarp({
        dz: { lat: +lat.value, lon: +lon.value }, dropAltitudeAglM: +alt.value,
        aircraftHeadingDeg: +hdg.value, aircraftSpeedKt: +spd.value,
        wind: { speedKt: +wind.value, fromDeg: +windDir.value }, canopy: canopy.value,
      });
      this.table(['Størrelse', 'Verdi'], [
        ['Slippunkt (CARP)', `${r.carp.lat.toFixed(5)}, ${r.carp.lon.toFixed(5)}`],
        ['Fra DZ', `${r.distanceFromDzM.toFixed(0)} m @ ${r.bearingFromDzDeg.toFixed(0)}°`],
        ['Flytid', `${r.timeOfFallS.toFixed(1)} s`],
        ['Vindavdrift', `${r.driftM.toFixed(0)} m mot ${r.driftBearingDeg.toFixed(0)}°`],
        ['Framkast', `${r.forwardThrowM.toFixed(0)} m`],
      ]);
    }));
  }

  // ── HLZ ────────────────────────────────────────────────────────────────
  private formHlz() {
    const f = this.formEl;
    const cls = selField<HeloClass>(f, 'Helikopterklasse',
      (['light', 'medium', 'heavy'] as HeloClass[]).map((c) => ({ v: c, t: HELO_CLASS_LABEL[c] })), 'medium');
    const dia = numField(f, 'Brukbar diameter (m)', 50);
    const slope = numField(f, 'Helning (°)', 3);
    const surf = selField<HlzSurface>(f, 'Underlag',
      ([['firm', 'Fast'], ['soft', 'Mykt'], ['snow', 'Snø'], ['sand', 'Sand'], ['water', 'Vann'], ['unknown', 'Ukjent']] as [HlzSurface, string][]).map(([v, t]) => ({ v, t })), 'firm');
    const wind = numField(f, 'Vind fra (°, valgfri)', 0);
    f.append(btn('Vurder HLZ', () => {
      const a = assessHlz({
        heloClass: cls.value as HeloClass, usableDiameterM: +dia.value, slopeDeg: +slope.value,
        surface: surf.value as HlzSurface, windFromDeg: +wind.value,
      });
      const out = div('fc-verdict');
      out.append(span(`fc-badge fc-${a.suitability}`, HLZ_SUITABILITY_LABEL[a.suitability]));
      if (a.recommendedApproachDeg !== undefined) out.append(span('fc-note', ` · innflyging ${a.recommendedApproachDeg.toFixed(0)}°`));
      this.outEl.textContent = ''; this.outEl.append(out);
      const ul = document.createElement('ul'); ul.className = 'fc-reasons';
      for (const r of a.reasons) { const li = document.createElement('li'); li.textContent = r; ul.append(li); }
      this.outEl.append(ul);
    }));
  }

  // ── GARS ───────────────────────────────────────────────────────────────
  private formGars() {
    const f = this.formEl;
    const lat = numField(f, 'Bredde (°)', 59.9127);
    const lon = numField(f, 'Lengde (°)', 10.7461);
    const prec = selField(f, 'Presisjon', [{ v: '30', t: '30′' }, { v: '15', t: '15′' }, { v: '5', t: '5′' }], '5');
    f.append(btn('Konverter til GARS', () => {
      const g = toGars(+lat.value, +lon.value, Number(prec.value) as 30 | 15 | 5);
      this.table(['Felt', 'Verdi'], [['GARS', g], ['Lat/Lon', `${(+lat.value).toFixed(4)}, ${(+lon.value).toFixed(4)}`]]);
    }));
  }

  private table(head: string[], rows: string[][]) {
    this.outEl.textContent = '';
    const t = document.createElement('table'); t.className = 'fc-table';
    const thead = document.createElement('tr');
    for (const h of head) { const th = document.createElement('th'); th.textContent = h; thead.append(th); }
    t.append(thead);
    for (const r of rows) {
      const tr = document.createElement('tr');
      for (const c of r) { const td = document.createElement('td'); td.textContent = c; tr.append(td); }
      t.append(tr);
    }
    this.outEl.append(t);
  }
}
